import languageCodes from './reddit-language-codes.json';
import { productAttributes } from './productAttributes';

export type MarketInferredLanguage =
  | { state: 'inferred'; code: string; detector_version: string }
  | { state: 'unknown'; reason: string; detector_version: string };

export interface MarketProductItem {
  evidence_id: string;
  observation_domain: "commerce" | "commerce_review" | "social" | "ad" | "search";
  source_system:
    | "amazon"
    | "meta_ad_library"
    | "reddit"
    | "x"
    | "youtube"
    | "facebook"
    | "instagram"
    | "google_search"
    | "google_trends";
  source_url: string | null;
  observed_at: string | null;
  data: Record<string, unknown>;
  engagement?: { name: string; value: number | null }[];
  keyword_match?: { state: 'matched' | 'excluded' | 'unknown'; reason: string; policy_version: string;
    results?: Array<{ result_index: number; url: string; state: 'matched' | 'excluded' | 'unknown'; reason: string; policy_version: string }> } | null;
  result_selection?: { state: 'matched' | 'excluded' | 'unknown'; reasons: string[]; policy_version: string; language?: MarketInferredLanguage } | null;
}

export interface MarketProductEvidencePage {
  monitor_id: string;
  content_trust: "external_untrusted";
  total_items: number;
  next_cursor: string | null;
  items: MarketProductItem[];
  selection_summary?: Record<string, number> | null;
}

const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const validKeywordMatch = (value: unknown): boolean => {
  if (value == null) return true;
  const validState = (row: Record<string, unknown>) => ['matched', 'excluded', 'unknown'].includes(String(row.state))
    && row.policy_version === 'keyword-selection/v1' && typeof row.reason === 'string' && row.reason.length > 0;
  if (!record(value) || !validState(value)) return false;
  if (value.results === undefined) return true;
  if (!Array.isArray(value.results)) return false;
  const indexes = new Set<number>();
  return value.results.every(row => {
    if (!record(row) || !validState(row) || typeof row.url !== 'string' || !Number.isSafeInteger(row.result_index)
      || Number(row.result_index) < 0 || indexes.has(Number(row.result_index))) return false;
    indexes.add(Number(row.result_index)); return true;
  });
};

const validLanguage = (value: unknown) => value === undefined || (record(value)
  && typeof value.detector_version === 'string' && value.detector_version.trim().length > 0
  && (value.state === 'inferred'
    ? typeof value.code === 'string' && languageCodes.includes(value.code)
    : value.state === 'unknown' && value.code === undefined && typeof value.reason === 'string' && value.reason.trim().length > 0));

export function validateProductEvidence(
  value: unknown,
  monitorId: string,
  domain: MarketProductItem['observation_domain'] = "commerce",
): MarketProductEvidencePage {
  if (
    !record(value) ||
    value.monitor_id !== monitorId ||
    value.content_trust !== "external_untrusted" ||
    !Number.isSafeInteger(value.total_items) ||
    Number(value.total_items) < 0 ||
    !(value.next_cursor === null || typeof value.next_cursor === "string") ||
    !Array.isArray(value.items) ||
    value.items.some(
      (item) =>
        !record(item) ||
        typeof item.evidence_id !== "string" ||
        !(domain === "commerce" || domain === "commerce_review"
          ? item.source_system === "amazon"
          : domain === "ad" ? item.source_system === "meta_ad_library"
          : domain === "search" ? ["google_search", "google_trends"].includes(String(item.source_system))
          : ["reddit", "x", "youtube", "facebook", "instagram"].includes(
              String(item.source_system),
            )) ||
        item.observation_domain !== domain ||
        !record(item.data) ||
        !validKeywordMatch(item.keyword_match) ||
        (item.result_selection != null && (!record(item.result_selection)
          || !['matched', 'excluded', 'unknown'].includes(String(item.result_selection.state))
          || typeof item.result_selection.policy_version !== 'string' || !item.result_selection.policy_version.trim()
          || !validLanguage(item.result_selection.language)
          || !Array.isArray(item.result_selection.reasons) || item.result_selection.reasons.some(reason => typeof reason !== 'string'))),
    )
  ) {
    throw new Error("Invalid product evidence response");
  }
  if (value.selection_summary != null && (!record(value.selection_summary)
      || Object.values(value.selection_summary).some(count => !Number.isSafeInteger(count) || Number(count) < 0)))
    throw new Error('Invalid evidence selection summary');
  if (domain === 'commerce') value.items.forEach(item => productAttributes(item.data));
  return value as unknown as MarketProductEvidencePage;
}
